/**
 * Full-onboarding form schema (FR-ONB-3). Companion to the M1
 * FR-ONB-1 minimum-info form; this one collects KYB-grade fields.
 */
import { z } from 'zod';
import { businessAddress, usStateCode } from './onboarding.js';

const usPhoneRegex = /^\+?1?[\s.-]?\(?(\d{3})\)?[\s.-]?(\d{3})[\s.-]?(\d{4})$/;
const einRegex = /^\d{2}-?\d{7}$/;
const routingRegex = /^\d{9}$/;
const accountRegex = /^\d{4,17}$/;
const dobRegex = /^\d{4}-\d{2}-\d{2}$/;
const ssn4Regex = /^\d{4}$/;

export const accountType = z.enum(['checking', 'savings']);

export const ultimateBeneficialOwner = z.object({
  fullName: z.string().trim().min(1).max(100),
  dateOfBirth: z.string().regex(dobRegex, 'Use YYYY-MM-DD'),
  ssnLast4: z.string().regex(ssn4Regex, 'Last 4 digits of SSN').optional(),
  ownershipPct: z.number().int().min(0).max(100),
  address: businessAddress,
});

export const uploadRef = z.object({
  kind: z.enum([
    'gov_id_front',
    'gov_id_back',
    'proof_of_address',
    'formation_certificate',
    'other',
  ]),
  r2Key: z.string().min(1),
  sha256: z.string().regex(/^[a-f0-9]{64}$/, 'SHA-256 in hex'),
  fileName: z.string().min(1).max(200),
  contentType: z.string().min(1).max(100),
});

export const onboardingCompleteInput = z.object({
  ein: z.string().regex(einRegex, 'EIN must be 9 digits, e.g. 12-3456789'),
  formationState: usStateCode,
  primaryContact: z.object({
    fullName: z.string().trim().min(1).max(100),
    email: z.string().trim().toLowerCase().email(),
    phone: z.string().trim().regex(usPhoneRegex, 'Enter a US phone number'),
  }),
  ubos: z.array(ultimateBeneficialOwner).min(1).max(4),
  bankAccount: z.object({
    routingNumber: z.string().regex(routingRegex, 'Routing number is 9 digits'),
    accountNumber: z.string().regex(accountRegex, 'Account number is 4–17 digits'),
    accountType,
  }),
  uploads: z.array(uploadRef).min(1),
  agreementAccepted: z.literal(true),
});
export type OnboardingCompleteInput = z.infer<typeof onboardingCompleteInput>;
